import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../styles/country.css";

const countries = [
  {
    code: "IN",
    name: "INDIA",
    flag: "https://flagcdn.com/in.svg",
    link: "https://www.cloudare.in/",
  },
  {
    code: "MY",
    name: "MALAYSIA",
    flag: "https://flagcdn.com/my.svg",
    link: "https://www.cloudare.in/tax/malaysia",
  },
  {
    code: "SA",
    name: "KSA",
    flag: "https://flagcdn.com/sa.svg",
    link: "https://www.cloudare.in/tax/ksa",
  },
  {
    code: "US",
    name: "USA",
    flag: "https://flagcdn.com/us.svg",
    link: "https://www.cloudare.in/staffing",
  },
  {
    code: "UAE",
    name: "UAE",
    flag: "https://flagcdn.com/ae.svg",
    link: "https://www.cloudare.in/tax/uae",
  },
  {
    code: "GB",
    name: "UK",
    flag: "https://flagcdn.com/gb.svg",
    link: "https://www.cloudare.in/",
  },
  {
    code: "SG",
    name: "SINGAPORE",
    flag: "https://flagcdn.com/sg.svg",
    link: "https://www.cloudare.in/peppol",
  },
  {
    code: "CA",
    name: "CANADA",
    flag: "https://flagcdn.com/ca.svg",
    link: "https://www.cloudare.in/staffing",
  },
];

const CountrySelect = ({ inNav = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Default to India and remember the user's manual choice.
  // We intentionally do NOT auto-detect via a third-party IP geolocation
  // service, to avoid sending visitor IPs to an external provider without
  // consent (GDPR / DPDP friendly). Users pick their region manually.
  useEffect(() => {
    const savedCountry = localStorage.getItem("selectedCountry");
    if (savedCountry) {
      try {
        setSelectedCountry(JSON.parse(savedCountry));
        return;
      } catch {
        // fall through to default
      }
    }
    setSelectedCountry(countries.find((c) => c.code === "IN"));
  }, []);

  const toggleDropdown = () => setIsOpen((o) => !o);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    localStorage.setItem("selectedCountry", JSON.stringify(country));
    window.open(country.link, "_self");
  };

  // Positioning logic for portal (when inNav === true)
  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const width = Math.round(rect.width);
    let left = Math.round(rect.left);

    // Clamp inside viewport
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));

    const top = Math.min(
      Math.round(rect.bottom + 6),
      window.innerHeight - margin
    );
    setMenuPos({ top, left, width });
  };

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();

    const handleScroll = () => updateMenuPosition();
    const handleResize = () => updateMenuPosition();
    const handleKeyDown = (e) => e.key === "Escape" && setIsOpen(false);
    const handleClickOutside = (e) => {
      const t = triggerRef.current;
      const d = dropdownRef.current;
      if (!t) return;
      if (t.contains(e.target) || d?.contains(e.target)) return;
      setIsOpen(false);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const DropdownList = (
    <div className="country-dropdown-inner">
      {countries.map((country) => (
        <div
          className="country-item"
          key={country.code}
          onClick={() => handleCountrySelect(country)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleCountrySelect(country)}
        >
          <img
            src={country.flag}
            alt={`${country.name} flag`}
            className="country-flag"
          />
          <span className="country-name">{country.name}</span>
        </div>
      ))}
    </div>
  );

  const renderDropdown = () => {
    if (!isOpen) return null;

    // Use portal when inside nav to avoid clipping
    if (inNav && typeof document !== "undefined") {
      return createPortal(
        <div
          ref={dropdownRef}
          className="country-dropdown country-dropdown-portal"
          style={{
            position: "fixed",
            top: `${menuPos.top}px`,
            left: `${menuPos.left}px`,
            width: `${menuPos.width}px`,
          }}
        >
          {DropdownList}
        </div>,
        document.body
      );
    }

    // Default placement (inside own container)
    return (
      <div ref={dropdownRef} className="country-dropdown">
        {DropdownList}
      </div>
    );
  };

  return (
    <div
      className="country-select-container"
      ref={triggerRef}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
    >
      <button
        type="button"
        onClick={toggleDropdown}
        className="country-select-box"
        aria-label="Select country"
      >
        {selectedCountry && (
          <div className="country-display">
            <img
              src={selectedCountry.flag}
              alt={`${selectedCountry.name} flag`}
              className="country-flag"
            />
            <span className="country-code">
              {selectedCountry.code === "UAE" ? "AE" : selectedCountry.code}
            </span>
          </div>
        )}
        <span className="country-arrow" aria-hidden="true">
          {isOpen ? "▲" : "▼"}
        </span>
      </button>

      {renderDropdown()}
    </div>
  );
};

export default CountrySelect;
